import React, { useState, useEffect } from 'react';
import { Box, CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navigation from './components/Navigation';
import ShiftManagement from './components/ShiftManagement';
import Roster from './components/Roster';
import App from './App';  // Import existing scheduler app
import AdminPanel from './components/AdminPanel';
import Itinerary from './components/Itinerary';
import DutyRoster from './components/DutyRoster';
import Guests from './components/Guests';
import ReceptionDesk from './components/ReceptionDesk';
import TherapistDesk from './components/TherapistDesk';
import DoctorDesk from './components/DoctorDesk';
import PackageScheduling from './components/PackageScheduling';
import Billing from './components/Billing';
import ActivityTimeTable from './components/ActivityTimeTable';
import AddonPackageManager from './components/AddonPackageManager';
import RetreatPackageManager from './components/RetreatPackageManager';
import UserAndRoleManagement from './components/UserAndRoleManagement';
import Reports from './components/Reports';
import Maintenance from './components/Maintenance';
import SystemConfig from './components/SystemConfig';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';

// Define all available tabs with their permission requirements
const AVAILABLE_TABS = [
  { id: 'scheduler', screen: 'scheduler', action: 'view' },
  { id: 'packages', screen: 'packages', action: 'view' },
  { id: 'duty', screen: 'duty', action: 'view' },
  { id: 'roster', screen: 'roster', action: 'view' },
  { id: 'therapist', screen: 'therapist', action: 'view' },
  { id: 'doctor', screen: 'doctor', action: 'view' },
  { id: 'reception', screen: 'reception', action: 'view' },
  { id: 'guests', screen: 'guests', action: 'view' },
  { id: 'itinerary', screen: 'itinerary', action: 'view' },
  { id: 'activities', screen: 'activities', action: 'view' },
  { id: 'billing', screen: 'billing', action: 'view' },
  { id: 'shifts', screen: 'shifts', action: 'view' },
  { id: 'addonPackages', screen: 'addonPackages', action: 'view' },
  { id: 'retreatPackages', screen: 'retreatPackages', action: 'view' },
  { id: 'reports', screen: 'reports', action: 'view' },
  { id: 'maintenance', screen: 'maintenance', action: 'view' },
  { id: 'userManagement', screen: 'user_management', action: 'view' },
  { id: 'admin', screen: 'admin', action: 'view' },
  { id: 'systemConfig', screen: 'admin', action: 'view' },
];

// Brand theme: gold primary and near-black text
const theme = createTheme({
  palette: {
    primary: {
      main: '#E4C984',
      light: '#f1e3b7',
      dark: '#cbb16f',
      contrastText: '#171717',
    },
    text: {
      primary: '#171717',
    },
  },
});

const AppContent = () => {
  const [activeTab, setActiveTab] = useState(null);
  const { isAuthenticated, loading, hasPermission } = useAuth();

  // Set default tab to first accessible tab when authenticated
  useEffect(() => {
    if (isAuthenticated && !loading && activeTab === null) {
      // Find first tab user has access to
      const firstAccessibleTab = AVAILABLE_TABS.find(tab => 
        hasPermission(tab.screen, tab.action)
      );
      
      if (firstAccessibleTab) {
        setActiveTab(firstAccessibleTab.id);
      } else {
        // Fallback to scheduler if no accessible tabs (shouldn't happen)
        setActiveTab('scheduler');
      }
    }
  }, [isAuthenticated, loading, hasPermission, activeTab]);

  // Show login if not authenticated
  if (!isAuthenticated && !loading) {
    return <Login onLoginSuccess={() => {}} />;
  }

  // Show loading while determining initial tab
  if (activeTab === null) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          Loading...
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <Navigation activeTab={activeTab} onTabChange={setActiveTab} />
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {activeTab === 'scheduler' && (
          <ProtectedRoute screen="scheduler" action="view">
            <App />
          </ProtectedRoute>
        )}
        {activeTab === 'packages' && (
          <ProtectedRoute screen="packages" action="view">
            <PackageScheduling />
          </ProtectedRoute>
        )}
        {activeTab === 'shifts' && (
          <ProtectedRoute screen="shifts" action="view">
            <ShiftManagement />
          </ProtectedRoute>
        )}
        {activeTab === 'guests' && (
          <ProtectedRoute screen="guests" action="view">
            <Guests onOpenItinerary={(guest) => setActiveTab('itinerary')} />
          </ProtectedRoute>
        )}
        {activeTab === 'roster' && (
          <ProtectedRoute screen="roster" action="view">
            <Roster />
          </ProtectedRoute>
        )}
        {activeTab === 'admin' && (
          <ProtectedRoute screen="admin" action="view">
            <AdminPanel />
          </ProtectedRoute>
        )}
        {activeTab === 'itinerary' && (
          <ProtectedRoute screen="itinerary" action="view">
            <Itinerary />
          </ProtectedRoute>
        )}
        {activeTab === 'duty' && (
          <ProtectedRoute screen="duty" action="view">
            <DutyRoster />
          </ProtectedRoute>
        )}
        {activeTab === 'reception' && (
          <ProtectedRoute screen="reception" action="view">
            <ReceptionDesk />
          </ProtectedRoute>
        )}
        {activeTab === 'therapist' && (
          <ProtectedRoute screen="therapist" action="view">
            <TherapistDesk />
          </ProtectedRoute>
        )}
        {activeTab === 'doctor' && (
          <ProtectedRoute screen="doctor" action="view">
            <DoctorDesk />
          </ProtectedRoute>
        )}
        {activeTab === 'billing' && (
          <ProtectedRoute screen="billing" action="view">
            <Billing />
          </ProtectedRoute>
        )}
        {activeTab === 'addonPackages' && (
          <ProtectedRoute screen="addonPackages" action="view">
            <AddonPackageManager />
          </ProtectedRoute>
        )}
        {activeTab === 'retreatPackages' && (
          <ProtectedRoute screen="retreatPackages" action="view">
            <RetreatPackageManager />
          </ProtectedRoute>
        )}
        {activeTab === 'activities' && (
          <ProtectedRoute screen="activities" action="view">
            <ActivityTimeTable />
          </ProtectedRoute>
        )}
        {activeTab === 'maintenance' && (
          <ProtectedRoute screen="maintenance" action="view">
            <Maintenance />
          </ProtectedRoute>
        )}
        {activeTab === 'reports' && (
          <ProtectedRoute screen="reports" action="view">
            <Reports />
          </ProtectedRoute>
        )}
        {activeTab === 'userManagement' && (
          <ProtectedRoute screen="user_management" action="view">
            <UserAndRoleManagement />
          </ProtectedRoute>
        )}
        {activeTab === 'systemConfig' && (
          <ProtectedRoute screen="admin" action="view">
            <SystemConfig />
          </ProtectedRoute>
        )}
      </Box>
    </Box>
  );
};

const AppWithShifts = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
};

export default AppWithShifts;
